import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { useData } from '../contexts/DataContext';
import { formatZAR } from '../utils/currency';
import { Sparkles, Heart, Plus, Search, PenTool, CheckCircle2 } from 'lucide-react';
export function BalloonsPage() {
  const {
    products
  } = useData();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  // Filter for balloon products only
  const balloonProducts = products.filter(product => product.category.toLowerCase() === 'balloons');
  // Apply search filter
  const filteredProducts = balloonProducts.filter(product => product.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleCustomize = (productId: string) => {
    // Navigate to occasions page to start customization flow
    navigate('/occasions');
  };
  const showcaseImages = ['https://cdn.magicpatterns.com/uploads/fVp4kcB1GCoFoMDdUouYKR/71f1e28d1b366ac4f58abe86dfcc85e7.jpg', 'https://cdn.magicpatterns.com/uploads/c4dX4YhvrqRvHYU1VW98kA/584babe5f4339cda959d4ccfeadc5321.jpg', 'https://cdn.magicpatterns.com/uploads/oMumz1w7LfKEyPNVMJvCxF/6983ba56ba37806d86ed37bafe6c2131.jpg', 'https://cdn.magicpatterns.com/uploads/cFX1torgBN2vSe4qYyksvp/9ac1a1faa5e8b3baf9b7d1455c01881c.jpg'];
  return <div className="min-h-screen bg-white" data-id="element-1618">
      <Navbar data-id="element-1619" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-gradient-to-b from-rose-50/50 to-white" data-id="element-1620">
        {/* Decorative Background */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-1621">
          <motion.div animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-20 left-[10%] w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-1622" />
          <motion.div animate={{
          y: [0, 30, 0],
          rotate: [0, -5, 0]
        }} transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute top-40 right-[10%] w-72 h-72 bg-rose-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-1623" />
          <motion.div animate={{
          y: [0, -15, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.5
        }} className="absolute bottom-20 left-[40%] w-56 h-56 bg-pink-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-1624" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1625">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-1626">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-rose-100 shadow-sm mb-6" data-id="element-1627">
              <Sparkles className="w-4 h-4 text-rose-400 mr-2" data-id="element-1628" />
              <span className="text-sm font-medium text-gray-600" data-id="element-1629">
                Personalized Just For You
              </span>
            </div>

            <h1 className="font-serif text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6" data-id="element-1630">
              Balloons That Make{' '}
              <span className="text-rose-500" data-id="element-1631">Moments Float</span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8" data-id="element-1632">
              From birthdays to celebrations, our custom balloons add that
              perfect touch of joy. Choose your favorite and make it uniquely
              yours.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1633">
              <Button size="lg" onClick={() => navigate('/occasions')} rightIcon={<PenTool className="w-5 h-5" data-id="element-1635" />} data-id="element-1634">
                Start Customizing
              </Button>
              <Button variant="outline" size="lg" data-id="element-1636">
                View All Products
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Personalized Showcase Section */}
      <section className="py-20 bg-white overflow-hidden" data-id="element-1637">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1638">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1639">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1640">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-rose-50 border border-rose-100 shadow-sm mb-6" data-id="element-1641">
                <Sparkles className="w-4 h-4 text-rose-500 mr-2" data-id="element-1642" />
                <span className="text-sm font-medium text-rose-900" data-id="element-1643">
                  Custom Creations
                </span>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-1644">
                Personalized Balloons <br data-id="element-1645" />
                <span className="text-rose-500" data-id="element-1646">Made Unique</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1647">
                Create unforgettable memories with our bespoke balloon
                arrangements. Add names, special dates, or heartfelt messages to
                make every occasion extra special.
              </p>

              <div className="flex flex-wrap items-center gap-6 mb-10" data-id="element-1648">
                <div className="bg-rose-50 px-6 py-4 rounded-2xl border border-rose-100 shadow-sm" data-id="element-1649">
                  <span className="block text-sm text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-1650">
                    Starting from
                  </span>
                  <span className="block text-4xl font-bold text-rose-600" data-id="element-1651">
                    R200
                  </span>
                </div>
                <div className="h-16 w-px bg-gray-200 hidden sm:block" data-id="element-1652"></div>
                <div className="flex flex-col gap-2" data-id="element-1653">
                  <span className="flex items-center text-gray-700 font-medium" data-id="element-1654">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" data-id="element-1655" />{' '}
                    Custom Names & Messages
                  </span>
                  <span className="flex items-center text-gray-700 font-medium" data-id="element-1656">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" data-id="element-1657" /> Any
                    Color Theme
                  </span>
                  <span className="flex items-center text-gray-700 font-medium" data-id="element-1658">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" data-id="element-1659" />{' '}
                    Unique Arrangements
                  </span>
                </div>
              </div>

              <Button size="lg" onClick={() => navigate('/occasions')} className="w-full sm:w-auto shadow-lg shadow-rose-200" rightIcon={<PenTool className="w-5 h-5" data-id="element-1661" />} data-id="element-1660">
                Start Customizing Now
              </Button>
            </motion.div>

            <div className="grid grid-cols-2 gap-4 sm:gap-6" data-id="element-1662">
              {showcaseImages.map((img, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.15
            }} className={`relative rounded-2xl overflow-hidden shadow-lg aspect-[4/5] group cursor-pointer ${idx % 2 === 1 ? 'mt-8' : ''}`} data-id="element-1663">
                  <img src={img} alt={`Personalized Balloon Example ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1664" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4" data-id="element-1665">
                    <span className="text-white font-medium text-sm flex items-center" data-id="element-1666">
                      <Heart className="w-4 h-4 mr-2 fill-current" data-id="element-1667" /> View
                      Design
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-gray-50" data-id="element-1668">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1669">
          <div className="text-center mb-12" data-id="element-1670">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4" data-id="element-1671">
              Explore Our Collection
            </h2>
            <div className="max-w-md mx-auto relative" data-id="element-1672">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-1673" />
              <input type="text" placeholder="Search balloons..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-white border-2 border-rose-100 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all shadow-sm" data-id="element-1674" />
            </div>
          </div>

          {/* Product Count */}
          <div className="mb-8 flex justify-between items-center" data-id="element-1675">
            <p className="text-gray-600" data-id="element-1676">
              <span className="font-semibold text-gray-900" data-id="element-1677">
                {filteredProducts.length}
              </span>{' '}
              balloon{filteredProducts.length !== 1 ? 's' : ''} available
            </p>
          </div>

          {/* Products Grid */}
          {filteredProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-1678">
              {filteredProducts.map((product, index) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-rose-50" data-id="element-1679">
                  {/* Image Container */}
                  <div className="relative aspect-square overflow-hidden bg-gray-100" data-id="element-1680">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110" data-id="element-1681" />

                    {/* Badges */}
                    {product.isNew && <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-rose-500 shadow-sm" data-id="element-1682">
                        NEW
                      </div>}

                    {/* Quick Actions */}
                    <div className="absolute top-4 right-4 opacity-0 transform translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" data-id="element-1683">
                      <button className="p-2 bg-white rounded-full shadow-md text-gray-400 hover:text-rose-500 transition-colors" data-id="element-1684">
                        <Heart className="w-5 h-5" data-id="element-1685" />
                      </button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-5" data-id="element-1686">
                    <div className="text-xs font-medium text-rose-500 mb-1 uppercase tracking-wider" data-id="element-1687">
                      {product.category}
                    </div>
                    <h3 className="font-serif text-lg font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem]" data-id="element-1688">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between mb-4" data-id="element-1689">
                      <span className="text-xl font-bold text-gray-900" data-id="element-1690">
                        {formatZAR(product.price)}
                      </span>
                      {product.stock > 0 ? <span className="text-xs text-green-600 font-medium" data-id="element-1691">
                          In Stock
                        </span> : <span className="text-xs text-red-600 font-medium" data-id="element-1692">
                          Out of Stock
                        </span>}
                    </div>

                    {/* Customize Button */}
                    <Button onClick={() => handleCustomize(product.id)} className="w-full" size="sm" leftIcon={<PenTool className="w-4 h-4" data-id="element-1694" />} data-id="element-1693">
                      Customize This
                    </Button>
                  </div>
                </motion.div>)}
            </div> : <div className="text-center py-16" data-id="element-1695">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1696">
                <Search className="w-8 h-8 text-rose-400" data-id="element-1697" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1698">
                No balloons found
              </h3>
              <p className="text-gray-600" data-id="element-1699">
                Try adjusting your search to find what you're looking for.
              </p>
            </div>}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-white to-rose-50/30" data-id="element-1700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1701">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1702">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-6" data-id="element-1703">
              <Sparkles className="w-8 h-8 text-rose-500" data-id="element-1704" />
            </div>

            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4" data-id="element-1705">
              Make It Personal
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-1706">
              Every balloon can be customized with names, messages, colors, and
              more. Create something truly special for your celebration.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1707">
              <Button size="lg" onClick={() => navigate('/occasions')} rightIcon={<PenTool className="w-5 h-5" data-id="element-1709" />} data-id="element-1708">
                Start Customizing
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/about')} data-id="element-1710">
                Learn More About Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Our Balloons */}
      <section className="py-16 bg-white" data-id="element-1711">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1712">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1713">
            {[{
            icon: <PenTool className="w-6 h-6" data-id="element-1714" />,
            title: 'Fully Customizable',
            description: 'Add names, dates, messages, and choose your perfect colors'
          }, {
            icon: <Heart className="w-6 h-6" data-id="element-1715" />,
            title: 'Premium Quality',
            description: 'High-quality balloons that last longer and look stunning'
          }, {
            icon: <Sparkles className="w-6 h-6" data-id="element-1716" />,
            title: 'Perfect for Any Occasion',
            description: 'Birthdays, weddings, anniversaries, and every celebration'
          }].map((feature, index) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="text-center p-6" data-id="element-1717">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-rose-100 rounded-full text-rose-500 mb-4" data-id="element-1718">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" data-id="element-1719">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1720">{feature.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-1721" />
    </div>;
}